import {
  calculateCollabageddonPricing,
  formatCurrency,
} from "@sellar-io/common";
import {
  Button,
  RadioSelector,
  RadioSelectorItem,
  H3,
  H5,
  H6,
} from "@sellar-io/components";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";
import {
  Icon,
  OrderSummaryPrice,
  ProfilePicture,
  SpinnerLegacy,
} from "../../components";
import { Alert } from "~/components/alert/Alert";
import { Badge } from "~/components/badge/Badge";
import { DietaryDisplayList } from "~/components/dietaryDisplayList/DietaryDisplayList";
import { NavigationBar } from "~/components/navigation/NavigationBar";
import { PaymentTermsIndicator } from "~/components/paymentTypeIndicator/PaymentTermsIndicator";
import { COLLABAGEDDON, ORDERS } from "~/constants/routes";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { useCollabageddonCreate } from "~/hooks/api/v2/collabageddon/useCollabageddonCreate";
import { useBuyerProduct } from "~/hooks/api/v2/product/useBuyerProduct";
import { useBuyerRelationshipList } from "~/hooks/api/v2/buyerRelationship/useBuyerRelationshipList";
import { useFocusedNavigation } from "~/hooks/useFocusedNavigation";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";

import { SellarCreditCheckDialog } from "../sellarCredit/components/SellarCreditCheckDialog/SellarCreditCheckDialog";
import { useCollabageddonStore } from "./CollabageddonStore";
import { CollabageddonProduct } from "./CollabageddonProductCard";
import { useLegalEntityCompanyCreditInfo } from "~/hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { zLayer } from "@sellar-io/components";

type CollabageddonPaymentType = "sellarCredit" | "manual" | undefined;
export const CollabageddonCheckout = () => {
  useFocusedNavigation(); // Hides all drawers - sidebar and basket drawer
  const { business } = useSellarCurrentBusiness();
  const companyCreditInfoQuery = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: business?.Retailer?.id ?? 0,
    },
    {
      enabled: Boolean(business?.Retailer?.id),
    },
  );
  const [creditDialogActive, setCreditDialogActive] = useState(false);
  const companyCreditInfo = companyCreditInfoQuery.data;
  const creditAmount = companyCreditInfo?.creditAmountAvailable;
  const showCreditBalance =
    companyCreditInfo?.status === "approved" &&
    companyCreditInfo.userCanViewSellarCreditBalance &&
    companyCreditInfo.userCanAccessSellarCredit;
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const navigate = useNavigate();
  const { selectedProducts } = useCollabageddonStore();
  const [paymentType, setPaymentType] = useState<CollabageddonPaymentType>();

  const isLoading = companyCreditInfoQuery.isPending;
  const { total } = calculateCollabageddonPricing(selectedProducts);

  const defaultPaymentType = isLoading
    ? undefined
    : !showCreditBalance || !creditAmount || creditAmount < total
      ? "manual"
      : "sellarCredit";
  const [previousDefaultPaymentType, setPreviousDefaultPaymentType] =
    useState(defaultPaymentType);
  if (defaultPaymentType !== previousDefaultPaymentType) {
    setPreviousDefaultPaymentType(defaultPaymentType);
    setPaymentType(defaultPaymentType);
  }

  const handleBackToCollabageddon = () => {
    navigate(COLLABAGEDDON);
  };

  if (!selectedProducts.length) {
    return <Navigate to={COLLABAGEDDON} />;
  }

  if (isLoading) {
    return <SpinnerLegacy />;
  }

  return (
    <>
      <NavigationBar
        onClickLeftIcon={handleBackToCollabageddon}
        leftIcon={"CHEVRON_LEFT"}
      />
      {!isDesktop ? (
        <OrderSummary
          selectedProducts={selectedProducts}
          onBackToCollabageddon={handleBackToCollabageddon}
          paymentType={paymentType}
        />
      ) : null}
      <div className="flex h-[calc(100vh-var(--nav-height))]">
        {/* Left side - Main content (scrollable) */}
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="mx-auto max-w-5xl p-4">
            <div className="mt-4 flex flex-col gap-2 rounded-lg border border-zinc-300 p-4">
              <H5>Payment terms</H5>
              <H6>
                Confirm your preferred payment type below. Payment will be
                handled by participating breweries after order creation.
              </H6>
              <RadioSelector
                value={paymentType || ""}
                onChange={(value) =>
                  setPaymentType(
                    value.currentTarget.value as CollabageddonPaymentType,
                  )
                }
              >
                <RadioSelectorItem
                  value={"sellarCredit"}
                  disabled={
                    !showCreditBalance || !creditAmount || creditAmount < total
                  }
                >
                  <div className="flex flex-col">
                    <div className="flex flex-nowrap gap-2">
                      <H5>Use my</H5>
                      <H5 className="flex items-center gap-1 bg-linear-to-br from-[#B557F1] to-[#D213A6] bg-clip-text font-semibold text-nowrap text-transparent">
                        <Icon
                          icon="SELLAR_NEW"
                          size={0.6669}
                          color="#D213A6"
                          className="shrink-0"
                        />
                        Sellar Credit
                      </H5>
                      <H5>account</H5>
                    </div>
                  </div>
                </RadioSelectorItem>
                <RadioSelectorItem value={"manual"}>
                  <div className="flex flex-col">
                    <H5>BACS manual invoice</H5>
                    <H6>
                      Your invoice will be issued on finalisation of your order
                    </H6>
                  </div>
                </RadioSelectorItem>
              </RadioSelector>
            </div>
          </div>
          <div className="mx-auto max-w-5xl px-4">
            <div
              className={`flex w-full flex-col gap-4 ${isDesktop ? "pb-16" : "pb-40"}`}
            >
              {/* Product Cards - Connected as single group */}
              <div className="flex flex-col overflow-hidden rounded-lg border border-zinc-300">
                {selectedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    collabageddonProduct={product}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Order Summary (fixed) */}
        {isDesktop ? (
          <div className="w-1/3 max-w-md border-l border-zinc-300 bg-zinc-100">
            <OrderSummary
              selectedProducts={selectedProducts}
              onBackToCollabageddon={handleBackToCollabageddon}
              paymentType={paymentType}
            />
          </div>
        ) : null}
      </div>

      <SellarCreditCheckDialog
        active={creditDialogActive}
        onDismiss={() => setCreditDialogActive(false)}
      />
    </>
  );
};

const ProductCard = ({
  collabageddonProduct,
}: {
  collabageddonProduct: CollabageddonProduct;
}) => {
  const productQuery = useBuyerProduct(collabageddonProduct.id);
  const buyerRelationshipQuery = useBuyerRelationshipList(
    {
      query: [
        {
          type: "supplierId",
          modifier: "EQUALS",
          value: collabageddonProduct.SupplierId,
        },
      ],
      limit: 1,
    },
    {
      refetchOnWindowFocus: false,
    },
  );
  const product = productQuery.data;
  const buyerRelationship = buyerRelationshipQuery.data?.pages[0]?.[0];
  const connection = buyerRelationship?.Relationship.BuyerRelationshipDetails;
  return (
    <div className="flex flex-col items-start justify-start gap-2 self-stretch border-t border-zinc-300 bg-white p-4 first:border-t-0">
      {/* Supplier Header */}
      <div className="inline-flex items-center justify-start gap-2 self-stretch first:px-2">
        <div data-size="40" className="relative h-10 w-10">
          <ProfilePicture
            logoUrl={product?.Supplier.Business.logoUrl || ""}
            name={product?.Supplier.Business.name}
          />
        </div>
        <div className="inline-flex flex-1 flex-col items-start justify-start">
          <div
            data-purpose="h2"
            className="inline-flex items-center justify-center"
          >
            <div className="justify-end font-['Inter'] text-base leading-normal font-semibold text-zinc-800">
              {product?.Supplier.Business.name}
            </div>
          </div>
          <div
            data-purpose="h6"
            className="inline-flex items-center justify-center self-stretch"
          >
            <div className="flex-1 justify-end font-['Inter'] text-xs leading-none font-normal text-zinc-500">
              Run by {product?.Supplier.Business.name}
            </div>
          </div>
        </div>
      </div>

      {/* Product Details */}
      <div className="flex flex-col items-start justify-start gap-2 self-stretch">
        <div className="flex flex-col items-end justify-start gap-4 self-stretch rounded-tl-lg rounded-tr-lg bg-white p-2">
          <div className="flex flex-col items-start justify-start self-stretch">
            <div className="inline-flex items-center justify-start gap-1">
              <H5>{product?.name} </H5>
              <DietaryDisplayList
                dietaryRequirements={{
                  VG: Boolean(product?.vegan),
                  GF: Boolean(product?.glutenFree),
                  LF: Boolean(product?.lactoseFree),
                }}
                mode="text"
              />
            </div>
            <div className="inline-flex items-center justify-start gap-2 text-sm">
              <H6 className={"text-sm"}>
                {product?.Parent?.kind}
                {" // "}
                {product?.Parent?.abv}% • {product?.containerType || "keg"}
              </H6>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end justify-start gap-4 self-stretch rounded-lg bg-neutral-50 p-2">
          <div className="inline-flex items-center justify-between self-stretch">
            <div className="inline-flex w-36 flex-col items-start justify-start">
              <div
                data-purpose="h5"
                className="inline-flex items-center justify-start self-stretch"
              >
                <p className="justify-end text-sm leading-tight font-semibold text-zinc-800">
                  Unit price
                </p>
              </div>
              <div
                data-purpose="h5"
                className="inline-flex items-center justify-start self-stretch"
              >
                <H6 className="text-xs">(ex. VAT)</H6>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2">
              <H3>{formatCurrency(collabageddonProduct.price)}</H3>
            </div>
          </div>
        </div>
        <PaymentTermsIndicator
          connection={connection}
          invoiceCreationStage={
            buyerRelationship?.Relationship.Supplier.invoiceCreationStage ||
            "confirmed"
          }
        />
      </div>
    </div>
  );
};

const OrderSummary = ({
  selectedProducts,
  onBackToCollabageddon,
  paymentType,
}: {
  selectedProducts: CollabageddonProduct[];
  onBackToCollabageddon: () => void;
  paymentType: CollabageddonPaymentType;
}) => {
  const navigate = useNavigate();
  const mutation = useCollabageddonCreate();
  const [showMore, setShowMore] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  // Calculate totals
  const { subTotal, taxAmount, total } =
    calculateCollabageddonPricing(selectedProducts);
  const onConfirm = () => {
    if (!paymentType) throw new Error("Payment type not selected");
    mutation.mutate(
      {
        data: {
          paymentType,
          products: selectedProducts,
        },
      },
      {
        onSuccess: () => navigate(ORDERS),
      },
    );
  };

  if (!isDesktop) {
    return (
      <>
        <div className="inline-flex flex-col justify-start gap-4 self-stretch p-4">
          <div className="inline-flex items-center justify-between self-stretch">
            <div className="flex items-center justify-start">
              <div
                data-purpose="h3"
                className="flex items-center justify-center"
              >
                <div className="justify-end font-['Inter'] text-xl leading-loose font-semibold text-zinc-800">
                  Order summary
                </div>
              </div>
            </div>
            <div
              className="flex cursor-pointer items-center justify-start gap-1"
              onClick={() => setShowMore(!showMore)}
            >
              <H6 className="justify-end text-base leading-normal font-normal text-zinc-800">
                View more
              </H6>
              <Icon icon={"CHEVRON_DOWN"} />
            </div>
          </div>
          {showMore ? (
            <div className={"flex flex-col gap-4"}>
              <OrderSummaryPrice
                cardContainerClassName={"p-0"}
                subTotal={formatCurrency(subTotal)}
                total={formatCurrency(total)}
                tax={{
                  name: "VAT",
                  percentage: "20",
                  total: formatCurrency(taxAmount),
                }}
                hideDelivery={true}
                discount={undefined}
              />
              <InfoCards />
            </div>
          ) : (
            <div className="inline-flex items-end justify-end">
              <div className="justify-end">
                <span className="text-base leading-normal font-normal text-zinc-500">
                  {selectedProducts.length} items:{" "}
                </span>
                <span className="text-base leading-normal font-semibold text-zinc-800">
                  {formatCurrency(total)}
                </span>
              </div>
            </div>
          )}
        </div>

        {mutation.isError ? (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(mutation.error)?.response?.data.message ??
              "Unknown error"
            }
          />
        ) : null}

        <div
          className={`fixed right-0 bottom-0 left-0 ${zLayer.pinned} border-t border-zinc-300 bg-white p-4`}
        >
          <div className="space-y-3">
            <Button
              className="w-full"
              disabled={!paymentType}
              loading={mutation.isPending}
              onClick={onConfirm}
            >
              CONFIRM MY ORDER
            </Button>
            <Button
              disabled={mutation.isPending}
              className="w-full"
              color="tertiary"
              onClick={onBackToCollabageddon}
            >
              BACK TO COLLABAGEDDON
            </Button>
          </div>
        </div>
      </>
    );
  }
  return (
    <div className="flex h-full flex-col overflow-y-auto bg-zinc-100 p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-zinc-800">Order summary</h2>
        <span className="text-sm text-zinc-600">
          {selectedProducts.length} items
        </span>
      </div>

      {/* Placeholder for order summary details */}
      <OrderSummaryPrice
        cardContainerClassName={"p-0"}
        subTotal={formatCurrency(subTotal)}
        total={formatCurrency(total)}
        tax={{
          name: "VAT",
          percentage: "20",
          total: formatCurrency(taxAmount),
        }}
        hideDelivery={true}
        discount={undefined}
      />

      {mutation.isError && (
        <Alert
          className={"my-2"}
          type={"error"}
          message={
            apiV2AssertError(mutation.error)?.response?.data.message ??
            "An unknown error occurred"
          }
        />
      )}

      {/* Buttons at bottom */}
      <div className="space-y-3 pt-4">
        <Button
          className="w-full"
          loading={mutation.isPending}
          onClick={onConfirm}
        >
          CONFIRM MY ORDER
        </Button>
        <Button
          disabled={mutation.isPending}
          className="w-full"
          color="tertiary"
          onClick={onBackToCollabageddon}
        >
          BACK TO COLLABAGEDDON
        </Button>
      </div>

      <InfoCards />
    </div>
  );
};

export const InfoCards = () => {
  return (
    <div className="flex flex-col gap-2">
      <div className={"flex flex-col gap-2 rounded-lg border bg-white p-4"}>
        <Badge icon={"TRUCK_OUTLINE"}>Delivery info</Badge>
        <p className={"text-sm"}>
          Your order will be collated into one shipment by the Sellar team and
          delivered to you on a pallet.
        </p>
      </div>
      <div className={"flex flex-col gap-2 rounded-lg border bg-white p-4"}>
        <Badge icon={"SALE"}>Discounts</Badge>
        <p className={"text-sm"}>
          If you choose to pay with Sellar Credit your discount will be applied
          as a credit note.
        </p>
      </div>
    </div>
  );
};
